// SFX Manager - Single source of truth for all sound effects
// Uses Web Audio API for reliable, low-latency playback

export class SFXManager {
    constructor(gameState) {
        this.gameState = gameState;
        this.revSound = null;
        this.engineSound = null;

        // Web Audio API
        this.audioContext = null;
        this.masterGain = null;
        this.soundBuffers = new Map();  // name -> AudioBuffer
        this.loadingPromises = new Map(); // name -> Promise (prevent duplicate loads)

        // Volume level (0-100, from GameState)
        this.volume = gameState.getSfxVolume();
        // Legacy mute check
        this.muted = this.volume === 0;
    }

    /**
     * Initialize Web Audio API context
     * Call this after user interaction (e.g., when game starts)
     * @param {AudioContext} existingContext - Optional existing context (e.g., from Three.js)
     */
    initAudioContext(existingContext = null) {
        if (this.audioContext) return;

        try {
            this.audioContext = existingContext || new (window.AudioContext || window.webkitAudioContext)();

            // Firefox requires explicit resume() during user gesture, even on fresh context
            if (this.audioContext.state !== 'running') {
                this.audioContext.resume();
            }

            // Create master gain node for volume control
            this.masterGain = this.audioContext.createGain();
            this.masterGain.connect(this.audioContext.destination);

            // Apply saved volume from game state (re-read in case state was loaded after constructor)
            this.volume = this.gameState.getSfxVolume();
            this.muted = this.volume === 0;
            this.masterGain.gain.value = this.volume / 100;

            console.log('[SFXManager] Initialized with volume:', this.volume);

            // iOS Safari requires playing audio during user gesture to "unlock" the context
            this._unlockiOSAudio();
        } catch (e) {
            console.error('[SFXManager] Failed to init Web Audio API:', e);
        }
    }

    /**
     * Play a silent buffer to unlock iOS Safari audio
     * Must be called during a user gesture
     */
    _unlockiOSAudio() {
        if (!this.audioContext) return;

        try {
            const buffer = this.audioContext.createBuffer(1, 1, 22050);
            const source = this.audioContext.createBufferSource();
            source.buffer = buffer;
            source.connect(this.audioContext.destination);
            source.start(0);
        } catch (e) {
            // Ignore errors - this is just a precaution
        }
    }

    /**
     * Fade in the master gain over a duration
     * @param {number} duration - Fade duration in milliseconds
     * @param {number} delay - Delay before starting fade in milliseconds
     */
    fadeIn(duration = 2000, delay = 0) {
        if (!this.masterGain || !this.audioContext || this.muted) return;

        // Don't fade if already at target volume (e.g., returning from background)
        const targetGain = this.volume / 100;
        if (this.masterGain.gain.value >= targetGain * 0.9) return;

        const startTime = this.audioContext.currentTime + (delay / 1000);
        const endTime = startTime + (duration / 1000);

        // Use Web Audio's built-in ramp for smooth fade
        this.masterGain.gain.setValueAtTime(0, startTime);
        this.masterGain.gain.linearRampToValueAtTime(targetGain, endTime);
    }

    /**
     * Instantly silence audio (for entering offline mode)
     */
    silence() {
        if (!this.masterGain) return;
        this.masterGain.gain.value = 0;
    }

    /**
     * Resume audio context if suspended (browser autoplay policy)
     */
    resumeContext() {
        if (this.audioContext?.state === 'suspended') {
            this.audioContext.resume();
        }
    }

    /**
     * Load a sound from URL into an AudioBuffer
     * @param {string} name - Sound name for playback
     * @param {string} url - URL to audio file
     * @returns {Promise<AudioBuffer>}
     */
    async loadSound(name, url) {
        // Return existing buffer if already loaded
        if (this.soundBuffers.has(name)) {
            return this.soundBuffers.get(name);
        }

        // Return existing promise if currently loading
        if (this.loadingPromises.has(name)) {
            return this.loadingPromises.get(name);
        }

        // Start loading
        const loadPromise = this._loadSoundBuffer(name, url);
        this.loadingPromises.set(name, loadPromise);

        try {
            const buffer = await loadPromise;
            this.loadingPromises.delete(name);
            return buffer;
        } catch (e) {
            this.loadingPromises.delete(name);
            throw e;
        }
    }

    async _loadSoundBuffer(name, url) {
        if (!this.audioContext) {
            console.warn('[SFXManager] AudioContext not initialized, cannot load sound:', name);
            return null;
        }

        try {
            const response = await fetch(url);
            const arrayBuffer = await response.arrayBuffer();
            const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
            this.soundBuffers.set(name, audioBuffer);
            console.log(`[SFXManager] Loaded sound: ${name}`);
            return audioBuffer;
        } catch (e) {
            console.error(`[SFXManager] Failed to load sound ${name}:`, e);
            return null;
        }
    }

    /**
     * Play a loaded sound
     * @param {string} name - Sound name
     * @param {object} options - { volume: 0-1, playbackRate: 0.5-2 }
     */
    playSound(name, options = {}) {
        if (this.muted) return;

        const buffer = this.soundBuffers.get(name);
        if (!buffer || !this.audioContext || !this.masterGain) {
            // Fallback: try legacy registered sounds
            if (this.legacySounds?.has(name)) {
                const audio = this.legacySounds.get(name);
                audio.currentTime = 0;
                audio.play().catch(() => {});
            }
            return;
        }

        // Resume context if needed (browsers may suspend it)
        if (this.audioContext.state === 'suspended') {
            this.audioContext.resume();
        }

        try {
            const source = this.audioContext.createBufferSource();
            source.buffer = buffer;

            if (options.playbackRate) {
                source.playbackRate.value = options.playbackRate;
            }

            const gainNode = this.audioContext.createGain();
            gainNode.gain.value = options.volume ?? 1;

            source.connect(gainNode);
            gainNode.connect(this.masterGain);
            source.start(0);
        } catch (e) {
            // Silently fail - sound is non-critical
        }
    }

    /**
     * Start a looping sound at fixed interval
     * @param {string} name - Sound name
     * @param {number} intervalMs - Milliseconds between plays
     * @param {object} options - Sound options
     * @returns {number} Loop ID for stopping
     */
    startSoundLoop(name, intervalMs, options = {}) {
        // Ensure context is running (Firefox can suspend it)
        this.resumeContext();

        // Play immediately
        this.playSound(name, options);

        // Then repeat at interval
        return setInterval(() => {
            this.playSound(name, options);
        }, intervalMs);
    }

    /**
     * Ensure audio context is ready (call on user interaction)
     * Firefox especially needs this
     */
    ensureReady() {
        if (this.audioContext?.state === 'suspended') {
            this.audioContext.resume();
        }
    }

    /**
     * Stop a sound loop
     * @param {number} loopId - ID from startSoundLoop
     */
    stopSoundLoop(loopId) {
        if (loopId) {
            clearInterval(loopId);
        }
    }

    // === Legacy support for HTML5 Audio elements ===

    legacySounds = new Map();

    registerSound(name, audioElement) {
        this.legacySounds.set(name, audioElement);
    }

    // === Rev/Engine sounds (special handling) ===

    setRevSound(revSound) {
        this.revSound = revSound;
    }

    setEngineSound(engineSound) {
        this.engineSound = engineSound;
    }

    playRev(speedPercentage) {
        if (this.muted || !this.revSound) return;
        this.revSound.playRev(speedPercentage);
    }

    playRevMax() {
        if (this.muted || !this.revSound) return;
        this.revSound.playRevMax();
    }

    playNewMile() {
        if (this.muted || !this.revSound) return;
        this.revSound.playNewMile();
    }

    // === Volume control ===

    getVolume() {
        return this.volume;
    }

    setVolume(volume) {
        this.volume = Math.max(0, Math.min(100, volume));
        this.muted = this.volume === 0;

        // Update master gain
        if (this.masterGain) {
            this.masterGain.gain.value = this.volume / 100;
        }

        if (this.engineSound) {
            if (this.muted) {
                if (this.engineSound.isPlaying) {
                    this.engineSound.stop();
                }
            } else {
                this.engineSound.start();
            }
        }

        this.gameState.setSfxVolume(this.volume);
    }

    isMuted() {
        return this.muted === true;
    }

    setMuted(muted) {
        this.setVolume(muted ? 0 : 100);
    }

    /**
     * Refresh volume from game state (call after state is replaced, e.g., on login)
     */
    refreshVolumeFromState() {
        const savedVolume = this.gameState.getSfxVolume();
        if (savedVolume !== this.volume) {
            console.log('[SFXManager] Refreshing volume from state:', savedVolume);
            this.volume = savedVolume;
            this.muted = this.volume === 0;
            if (this.masterGain) {
                this.masterGain.gain.value = this.volume / 100;
            }
        }
    }

    toggleMute() {
        this.setMuted(!this.muted);
        return this.muted;
    }

    // === Engine sound ===

    updateEngineSound(speed) {
        if (!this.engineSound) return;
        if (this.muted) return;
        this.engineSound.update(speed);
    }

    startEngineSound() {
        if (!this.engineSound) return;
        if (this.muted) return;
        this.engineSound.start();
    }
}
